/* Borderline winter 26/27 · fit tools (P6).
   One set of sizing rules for the product-page finders (assets/bl-pdp.js reads window.BLFit) and the three
   "Find my …" pages (sections/bl-finder.liquid → <bl-finder>). Hard-coded rules for now; an AI fitter can take over
   lengthTarget / bootAdvice / the helmet table later without touching either page.
   - lengthTarget({ kind: 'ski' | 'board', kg, cm, level, terrain }) → { target, min, max, why[] }
   - toMondo(n, sys) (sys 0 foot length cm, 1 US men, 2 US women, 3 EU) · bootSizesOf(value) · recommendBoot(mondo, values)
   - bootAdvice({ kind, width, level }) → { last, flex } keys for the page's words
   - helmetRange(value) · helmetLetter(cm) · recommendHelmet(cm, values) · recommendApparel(h, kg, fleece, values)
   - lengthOf(value) → { cm, wide } · nearestLength(target, values) */

const EU_MONDO = [[35, 22], [35.5, 22.5], [36, 23], [37, 23.5], [37.5, 24], [38, 24.5], [39, 25], [40, 25.5], [40.5, 26], [41, 26.5], [42, 27], [42.5, 27.5], [43, 28], [44, 28.5], [44.5, 29], [45, 29.5], [46, 30], [46.5, 30.5], [47, 31], [47.5, 31.5], [48, 32]];
const LETTERS = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL'];
const LEVELS = ['beginner', 'intermediate', 'advanced', 'expert'];
/** Head circumference (cm) by helmet letter; between two sizes the smaller one (pads settle). */
const HELMET_TABLE = [['XS', 49, 52], ['S', 52, 55], ['M', 55, 59], ['L', 59, 63], ['XL', 63, 65]];

/* ---------- length (skis, snowboards) ---------- */

/** "155W cm" → { cm: 155, wide: true }. @param {string} value */
function lengthOf(value) {
  const m = String(value).match(/(\d{2,3}(?:\.\d)?)\s*(w)?/i);
  return m ? { cm: parseFloat(m[1]), wide: !!m[2] } : null;
}

/**
 * The usual height for a weight, children to adults: [kg, cm] points, read between them and held at both ends. Owner
 * 10/21: the old stand-in (the height at BMI 22) grew without limit — 100 kg read as 213 cm and gave 202–208 cm skis —
 * while a heavier rider is wider, not taller; the weight step below adds length only when the real height is known.
 */
const HEIGHT_FOR_WEIGHT = [[15, 100], [20, 115], [25, 125], [30, 135], [35, 143], [40, 150], [45, 155], [55, 163], [65, 170], [75, 175], [85, 179], [95, 182], [105, 184]];

/** @param {number} kg */
function heightForWeight(kg) {
  const points = HEIGHT_FOR_WEIGHT;
  const first = points[0];
  const last = points[points.length - 1];
  if (!first || !last) return 0;
  if (kg <= first[0]) return first[1];
  if (kg >= last[0]) return last[1];
  for (let index = 1; index < points.length; index++) {
    const [k1, h1] = /** @type {number[]} */ (points[index]);
    const [k0, h0] = /** @type {number[]} */ (points[index - 1]);
    if (kg <= k1) return Math.round(h0 + ((kg - k0) * (h1 - h0)) / (k1 - k0));
  }
  return last[1];
}

/**
 * A length range from height, weight, level and terrain. Without a height, the usual height for the weight
 * (HEIGHT_FOR_WEIGHT) stands in; without a weight, no weight step. `why` lists each step for the page's words.
 * @param {{ kind?: string, kg?: number, cm?: number, level?: string, terrain?: string }} input
 */
function lengthTarget(input) {
  const board = input.kind === 'board';
  const kg = Number(input.kg) || 0;
  let h = Number(input.cm) || 0;
  const guessed = !h;
  if (!h && kg) h = heightForWeight(kg);
  if (!h) return null;
  const lvl = LEVELS.includes(String(input.level)) ? LEVELS.indexOf(String(input.level)) : 1;
  /** @type {Array<{ key: string, [k: string]: any }>} */
  const why = [];
  let target;
  if (board) {
    const base = Math.round(h * 0.88);
    why.push({ key: guessed ? 'base_board_kg' : 'base_board', h, v: base, kg });
    const d = [-3, 0, 2, 3][lvl];
    target = base + d;
    if (d) why.push({ key: 'level', level: LEVELS[lvl], d });
  } else {
    const base = h + [-15, -8, -3, 2][lvl];
    why.push({ key: guessed ? 'base_ski_kg' : 'base_ski', h, v: base, kg, level: LEVELS[lvl] });
    target = base;
  }
  if (!guessed && kg) {
    const ratio = kg / (22 * (h / 100) ** 2);
    const d = ratio > 1.15 ? (board ? 3 : 4) : ratio < 0.85 ? (board ? -3 : -4) : 0;
    target += d;
    why.push({ key: d > 0 ? 'heavy' : d < 0 ? 'light' : 'average', kg, d });
  }
  const terrain = String(input.terrain || 'all');
  // terrains (all, carve, powder, park) and the narrower styles the page passes instead when one is pressed: the
  // Carving / Racing / Touring steps hang on those sub pills (owner 10/20), Carving & racing alone changes nothing
  const t = /** @type {Record<string, number>} */ ({ all: 0, park: board ? -3 : -5, powder: board ? 4 : 5, carve: 0, carving: board ? 2 : -3, racing: board ? 2 : -3, touring: board ? 0 : -5 })[terrain] || 0;
  target += t;
  if (t) why.push({ key: terrain, d: t });
  target = Math.round(target);
  return { target, min: target - 3, max: target + 3, why };
}

/** The value whose length is nearest the target (a tie goes to the shorter). @param {number} target @param {string[]} values */
function nearestLength(target, values) {
  let best = null;
  let bd = Infinity;
  for (const v of values) {
    const l = lengthOf(v);
    if (!l) continue;
    const d = Math.abs(l.cm - target);
    if (d < bd || (d === bd && best && l.cm < /** @type {any} */ (lengthOf(best)).cm)) {
      bd = d;
      best = v;
    }
  }
  return best;
}

/* ---------- boots ---------- */

/** Mondo from a street size. @param {number} n @param {number} sys 0 foot cm, 1 US men, 2 US women, 3 EU */
function toMondo(n, sys) {
  if (!n) return null;
  if (sys === 0) return Math.ceil(n * 2) / 2;
  if (sys === 1) return n + 18;
  if (sys === 2) return n + 17;
  let best = EU_MONDO[0];
  for (const row of EU_MONDO) if (Math.abs(row[0] - n) < Math.abs(best[0] - n)) best = row;
  return Math.abs(best[0] - n) > 2 ? null : best[1];
}

/** The Mondo sizes a size value covers: "27/27.5" → [27, 27.5], "255 mm" → [25.5], "26.5" → [26.5]. @param {string} value */
function bootSizesOf(value) {
  const v = String(value);
  const mm = v.match(/(\d{3})\s*mm/i);
  if (mm) return [Number(mm[1]) / 10];
  return (v.match(/\d{2}(?:\.\d)?/g) || []).map(Number).filter((n) => n >= 15 && n <= 35);
}

/** The boot value that holds the Mondo, else the nearest from above. @param {number} mondo @param {string[]} values */
function recommendBoot(mondo, values) {
  const parsed = values.map((v) => ({ v, n: bootSizesOf(v) })).filter((x) => x.n.length);
  const exact = parsed.find((x) => x.n.includes(mondo));
  if (exact) return exact.v;
  const above = parsed.filter((x) => Math.max(...x.n) >= mondo).sort((a, b) => Math.min(...a.n) - Math.min(...b.n))[0];
  return above ? above.v : parsed.length ? parsed[parsed.length - 1].v : null;
}

/**
 * Last and flex for a boot: the keys the page turns into words ("last_narrow", "flex_ski_intermediate" …).
 * @param {{ kind?: string, width?: string, level?: string }} input
 */
function bootAdvice(input) {
  const level = LEVELS.includes(String(input.level)) ? String(input.level) : 'intermediate';
  const width = ['narrow', 'regular', 'wide'].includes(String(input.width)) ? String(input.width) : 'regular';
  const kind = input.kind === 'board' ? 'board' : 'ski';
  /** last in mm per width (ski boots); the band a fitter starts from */
  const last = /** @type {Record<string, number[]>} */ ({ narrow: [97, 98], regular: [100, 100], wide: [102, 104] })[width];
  return { kind, width, level, last, lastKey: `last_${width}`, flexKey: `flex_${kind}_${level}`, fitKey: level === 'advanced' || level === 'expert' ? 'fit_performance' : 'fit_comfort' };
}

/* ---------- helmets ---------- */

/** A helmet size value's head range: "M (55-59)" / "55-59" / "XS-S/51-54" / "M" / "M/L". @param {string} value */
function helmetRange(value) {
  const v = String(value);
  const m = v.match(/(\d{2}(?:\.\d)?)\s*[–—-]\s*(\d{2}(?:\.\d)?)/);
  if (m) return [parseFloat(m[1]), parseFloat(m[2])];
  // letters only ("M", "M/L", "XS-S"): a word like "One size" is not a helmet size
  if (!/^[\sXSML/-]+$/i.test(v)) return null;
  const letters = v.toUpperCase().match(/X{0,2}[SML]/g);
  if (!letters) return null;
  const rows = letters.map((l) => HELMET_TABLE.find((r) => r[0] === l)).filter(Boolean);
  if (!rows.length) return null;
  return [Math.min(...rows.map((r) => /** @type {any} */ (r)[1])), Math.max(...rows.map((r) => /** @type {any} */ (r)[2]))];
}

/** The letter for a head (between two sizes, the smaller). @param {number} cm */
function helmetLetter(cm) {
  const row = HELMET_TABLE.find((r) => cm >= r[1] && cm <= r[2]);
  if (row) return row[0];
  return cm < HELMET_TABLE[0][1] ? HELMET_TABLE[0][0] : HELMET_TABLE[HELMET_TABLE.length - 1][0];
}

/** The helmet value for a head: a range that holds it (the smaller between two), else the letter. */
function recommendHelmet(/** @type {number} */ cm, /** @type {string[]} */ values) {
  const ranged = values
    .map((v) => {
      const m = String(v).match(/(\d{2}(?:\.\d)?)\s*[–—-]\s*(\d{2}(?:\.\d)?)/);
      return m ? { v, lo: parseFloat(m[1]), hi: parseFloat(m[2]) } : null;
    })
    .filter(Boolean);
  if (ranged.length) {
    const hits = ranged.filter((r) => r && cm >= r.lo && cm <= r.hi);
    if (hits.length) return /** @type {any} */ (hits[0]).v;
    const dist = (/** @type {any} */ r) => Math.min(Math.abs(cm - r.lo), Math.abs(cm - r.hi));
    const nearest = ranged.slice().sort((a, b) => dist(a) - dist(b))[0];
    return nearest ? /** @type {any} */ (nearest).v : null;
  }
  const letter = helmetLetter(cm);
  return values.find((v) => String(v).trim().toUpperCase() === letter) || values.find((v) => String(v).trim().toUpperCase().startsWith(letter)) || null;
}

/* ---------- clothing ---------- */

/** The prototype's apparel rule (content.js recommendSize). */
function recommendApparel(/** @type {number} */ h, /** @type {number} */ kg, /** @type {boolean} */ fleece, /** @type {string[]} */ values) {
  const order = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];
  const upper = values.map((v) => String(v).trim().toUpperCase());
  if (!upper.some((v) => LETTERS.includes(v))) return null;
  let i = kg < 52 ? 0 : kg < 62 ? 1 : kg < 72 ? 2 : kg < 84 ? 3 : 4;
  if (h > 178) i++;
  if (h < 158) i--;
  if (fleece) i++;
  i = Math.max(0, Math.min(order.length - 1, i));
  const hit = upper.indexOf(order[i]);
  if (hit > -1) return values[hit];
  let best = -1;
  let bd = 99;
  upper.forEach((s, k) => {
    const d = Math.abs(order.indexOf(s) - i);
    if (order.includes(s) && d < bd) {
      bd = d;
      best = k;
    }
  });
  return best > -1 ? values[best] : null;
}

const BLFit = { EU_MONDO, LETTERS, LEVELS, HELMET_TABLE, lengthOf, lengthTarget, nearestLength, toMondo, bootSizesOf, recommendBoot, bootAdvice, helmetRange, helmetLetter, recommendHelmet, recommendApparel };
/** @type {any} */ (window).BLFit = BLFit;

/* ---------- the Find my … pages ---------- */

/** @param {unknown} value */
const esc = (value) => String(value).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] || c);

/** "+3" / "−4" for the why lines. @param {number} d */
const signed = (d) => (d > 0 ? `+${d}` : `−${Math.abs(d)}`);

class BlFinder extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;
  /** @type {any} */
  #data = null;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    try {
      this.#data = JSON.parse((this.querySelector('script[data-blf]') || {}).textContent || '{}');
    } catch (error) {
      this.#data = {};
    }
    const kind = new URLSearchParams(location.search).get('kind');
    if (kind === 'board' || kind === 'ski') this.#setChoice('kind', kind);
    this.addEventListener('input', () => this.#update(), { signal });
    this.addEventListener('change', () => this.#update(), { signal });
    this.addEventListener('click', (event) => {
      const pill = event.target instanceof Element ? event.target.closest('[data-blf-choice]') : null;
      if (!(pill instanceof HTMLElement)) return;
      const group = pill.dataset.blfChoice || '';
      // a narrower style pressed again lets go (back to the whole terrain)
      if (group === 'sub' && pill.classList.contains('is-on')) this.#setChoice('sub', '');
      else this.#setChoice(group, pill.dataset.value || '');
      this.#update();
    }, { signal });
    this.querySelector('form')?.addEventListener('submit', (event) => event.preventDefault(), { signal });
    this.#update();
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  get tool() {
    return this.dataset.tool || 'length';
  }

  /** A pill group: one pressed. @param {string} group @param {string} value */
  #setChoice(group, value) {
    for (const pill of this.querySelectorAll(`[data-blf-choice="${group}"]`)) {
      const on = /** @type {HTMLElement} */ (pill).dataset.value === value;
      pill.classList.toggle('is-on', on);
      pill.setAttribute('aria-pressed', String(on));
    }
  }

  /** @param {string} group */
  #choice(group) {
    const on = this.querySelector(`[data-blf-choice="${group}"].is-on`);
    return on instanceof HTMLElement ? on.dataset.value || '' : '';
  }

  /**
   * A field's number; 0 when empty, or when outside the field's own min / max (then marked aria-invalid), so a
   * height of 999 or a head of -1 counts as not filled in instead of giving a nonsense result.
   * @param {string} name
   */
  #num(name) {
    const input = this.querySelector(`[name="${name}"]`);
    if (input instanceof HTMLSelectElement) return parseFloat(input.value) || 0;
    if (!(input instanceof HTMLInputElement)) return 0;
    const raw = input.value.trim();
    const value = parseFloat(raw);
    const min = parseFloat(input.min);
    const max = parseFloat(input.max);
    const out = raw !== '' && (!Number.isFinite(value) || (Number.isFinite(min) && value < min) || (Number.isFinite(max) && value > max));
    if (out) input.setAttribute('aria-invalid', 'true');
    else input.removeAttribute('aria-invalid');
    return out ? 0 : value || 0;
  }

  /** The page's sentence for a key, with [h] [v] [kg] [d] [level] [mondo] [cm] [size] filled. @param {string} key @param {Record<string, any>} [vars] */
  #say(key, vars = {}) {
    const words = (this.#data && this.#data.rules) || {};
    let line = String(words[key] || '');
    const levels = (this.#data && this.#data.levels) || {};
    for (const [k, v] of Object.entries(vars)) {
      const shown = k === 'd' ? signed(Number(v)) : k === 'level' ? String(levels[v] || v).toLowerCase() : v;
      line = line.split(`[${k}]`).join(String(shown));
    }
    return line;
  }

  #update() {
    const tool = this.tool;
    const kind = this.#choice('kind') || 'ski';
    this.dataset.kind = kind;
    // a terrain with nothing in stock for this sport is not offered (All-mountain always is)
    if (tool === 'length') {
      const code = kind === 'board' ? 'b' : 'a';
      const wrap = document.querySelector(`[data-blf-picks="${this.id}"]`);
      const styles = new Set();
      for (const li of wrap ? wrap.querySelectorAll(`[data-blf-pick][data-kind="${code}"]`) : []) {
        for (const st of (/** @type {HTMLElement} */ (li).dataset.styles || '').split('|')) if (st) styles.add(st);
      }
      for (const pill of this.querySelectorAll('[data-blf-choice="terrain"]')) {
        const value = /** @type {HTMLElement} */ (pill).dataset.value || '';
        const off = value !== 'all' && !styles.has(value);
        /** @type {HTMLElement} */ (pill).hidden = off;
        if (off && pill.classList.contains('is-on')) this.#setChoice('terrain', 'all');
      }
      // the narrower styles of the chosen terrain that are in stock (specs.subcategories)
      const terrain = this.#choice('terrain') || 'all';
      const subsHere = new Set();
      for (const li of wrap ? wrap.querySelectorAll(`[data-blf-pick][data-kind="${code}"]`) : []) {
        const el = /** @type {HTMLElement} */ (li);
        if (!(el.dataset.styles || '').split('|').includes(terrain)) continue;
        for (const sb of (el.dataset.subs || '').split('|')) if (sb) subsHere.add(sb);
      }
      for (const pill of this.querySelectorAll('[data-blf-choice="sub"]')) {
        const el = /** @type {HTMLElement} */ (pill);
        const off = el.dataset.parent !== terrain || !subsHere.has(el.dataset.value || '');
        el.hidden = off;
        if (off && el.classList.contains('is-on')) {
          el.classList.remove('is-on');
          el.setAttribute('aria-pressed', 'false');
        }
      }
    }
    let result = null;
    if (tool === 'length') result = this.#length(kind);
    else if (tool === 'boot') result = this.#boot(kind);
    else result = this.#helmet();
    this.#paint(result);
    this.#picks(result, kind);
  }

  /** @param {string} kind */
  #length(kind) {
    const cm = this.#num('cm');
    const kg = this.#num('kg');
    // a number outside its field's range (flagged by #num) is a typo, not a missing value: no estimate from the
    // other field until it is corrected
    if (this.querySelector('[name="cm"][aria-invalid="true"], [name="kg"][aria-invalid="true"]')) return null;
    if (!cm && !kg) return null;
    const level = this.#choice('level') || 'intermediate';
    const terrain = this.#choice('terrain') || 'all';
    const sub = this.#choice('sub');
    const t = lengthTarget({ kind: kind === 'board' ? 'board' : 'ski', cm, kg, level, terrain: sub || terrain });
    if (!t) return null;
    return {
      big: `${t.min}–${t.max} cm`,
      sub: [this.#label('kind', kind), this.#label('level', level), this.#label('terrain', terrain), sub ? this.#label('sub', sub) : ''].filter(Boolean).join(' · '),
      why: t.why.map((w) => this.#say(w.key, w)),
      match: (/** @type {string} */ value) => {
        const l = lengthOf(value);
        if (!l || l.cm < t.min - 1 || l.cm > t.max + 1) return null;
        return Math.abs(l.cm - t.target);
      },
    };
  }

  /** @param {string} kind */
  #boot(kind) {
    const n = this.#num('shoe');
    const sys = this.#num('sys');
    const mondo = toMondo(n, sys);
    // the street-size field's 3–50 spans every system: US men's 50 would still be "Mondo 68"; boots run Mondo 15–35
    const ok = !!mondo && mondo >= 15 && mondo <= 35;
    if (n && !ok) this.querySelector('[name="shoe"]')?.setAttribute('aria-invalid', 'true');
    if (!ok) return null;
    const level = this.#choice('level') || 'intermediate';
    const width = this.#choice('width') || 'regular';
    const advice = bootAdvice({ kind: kind === 'board' ? 'board' : 'ski', width, level });
    const why = [this.#say('mondo', { mondo }), this.#say(kind === 'board' ? `width_${width}` : advice.lastKey), this.#say(advice.flexKey), this.#say(advice.fitKey)].filter(Boolean);
    return {
      big: `${this.#word('mondo_label')} ${mondo}`,
      sub: [this.#label('kind', kind), this.#label('width', width), this.#label('level', level)].filter(Boolean).join(' · '),
      why,
      match: (/** @type {string} */ value) => {
        const sizes = bootSizesOf(value);
        if (!sizes.length) return null;
        const d = Math.min(...sizes.map((s) => Math.abs(s - mondo)));
        return d <= 0.25 ? d : null;
      },
      last: kind === 'board' ? null : advice.last,
    };
  }

  #helmet() {
    const cm = this.#num('head');
    for (const row of this.querySelectorAll('[data-blf-size]')) row.classList.remove('is-on');
    if (!cm) return null;
    const letter = helmetLetter(cm);
    const row = this.querySelector(`[data-blf-size="${letter}"]`);
    if (row) row.classList.add('is-on');
    return {
      big: `${this.#word('size_label')} ${letter}`,
      sub: `${cm} cm`,
      why: [this.#say('head', { cm, size: letter }), this.#say('between')].filter(Boolean),
      match: (/** @type {string} */ value) => {
        const r = helmetRange(value);
        if (!r || cm < r[0] - 0.5 || cm > r[1] + 0.5) return null;
        return Math.abs(cm - (r[0] + r[1]) / 2);
      },
    };
  }

  /** @param {string} group @param {string} value */
  #label(group, value) {
    const pill = this.querySelector(`[data-blf-choice="${group}"][data-value="${value}"]`);
    return pill ? (pill.textContent || '').trim() : '';
  }

  /** @param {string} key */
  #word(key) {
    return String(((this.#data && this.#data.words) || {})[key] || '');
  }

  /** @param {any} result */
  #paint(result) {
    const box = this.querySelector('[data-blf-result]');
    if (!(box instanceof HTMLElement)) return;
    box.classList.toggle('is-got', !!result);
    const big = box.querySelector('[data-blf-big]');
    const sub = box.querySelector('[data-blf-sub]');
    const why = box.querySelector('[data-blf-why]');
    const whyWrap = box.querySelector('[data-blf-why-wrap]');
    if (big) big.textContent = result ? result.big : this.#word('empty');
    if (sub) sub.textContent = result ? result.sub : '';
    if (why) why.innerHTML = result ? result.why.map((/** @type {string} */ w) => `<li>${esc(w)}</li>`).join('') : '';
    if (whyWrap instanceof HTMLElement) whyWrap.hidden = !result || !result.why.length;
  }

  /** In-stock products that come in the result, closest first. @param {any} result @param {string} kind */
  #picks(result, kind) {
    const wrap = document.querySelector(`[data-blf-picks="${this.id}"]`);
    if (!(wrap instanceof HTMLElement)) return;
    const grid = wrap.querySelector('[data-blf-grid]');
    const empty = wrap.querySelector('[data-blf-empty]');
    const all = wrap.querySelector('[data-blf-all]');
    const count = wrap.querySelector('[data-blf-count]');
    if (!grid) return;
    const max = Number(wrap.dataset.max || 8);
    const moreWrap = wrap.querySelector('[data-blf-more]');
    const moreGrid = wrap.querySelector('[data-blf-grid-more]');
    const cards = [...wrap.querySelectorAll('[data-blf-pick]')].map((li) => /** @type {HTMLElement} */ (li));
    /** @type {Array<{ li: HTMLElement, score: number }>} */
    const hits = [];
    for (const li of cards) {
      li.hidden = true;
      if (!result) continue;
      if (li.dataset.kind && li.dataset.kind !== (this.tool === 'helmet' ? 'a' : kind === 'board' ? 'b' : 'a')) continue;
      const sizes = (li.dataset.sizes || '').split('|').filter(Boolean);
      let best = null;
      for (const s of sizes) {
        const d = result.match(s);
        if (d !== null && (best === null || d < best)) best = d;
      }
      if (best === null) continue;
      let score = best;
      if (result.last && li.dataset.last) {
        const lm = parseFloat(li.dataset.last);
        const [lo, hi] = result.last;
        score += lm < lo ? (lo - lm) / 2 : lm > hi ? (lm - hi) / 2 : 0;
      }
      hits.push({ li, score });
    }
    hits.sort((a, b) => a.score - b.score);
    // the length page: the chosen terrain first (specs.categories — a product can sit in several — else specs.subtype,
    // else the strongest ride_* meter); under four of those, the rest of the length follows under its own small title
    let main = hits;
    /** @type {typeof hits} */
    let rest = [];
    if (this.tool === 'length' && moreGrid) {
      const terrain = this.#choice('terrain') || 'all';
      const sub = this.#choice('sub');
      const has = (/** @type {HTMLElement} */ li) => (li.dataset.styles || '').split('|').includes(terrain) && (!sub || (li.dataset.subs || '').split('|').includes(sub));
      main = hits.filter((h) => has(h.li));
      rest = hits.filter((h) => !has(h.li));
    }
    const shown = main.slice(0, max);
    shown.forEach((h) => {
      h.li.hidden = false;
      grid.append(h.li);
    });
    const extra = shown.length < 4 ? rest.slice(0, max - shown.length) : [];
    extra.forEach((h) => {
      h.li.hidden = false;
      if (moreGrid) moreGrid.append(h.li);
    });
    if (grid instanceof HTMLElement) grid.hidden = !shown.length;
    if (moreWrap instanceof HTMLElement) {
      moreWrap.hidden = !result || !extra.length;
      const title = moreWrap.querySelector('[data-blf-more-title]');
      if (title instanceof HTMLElement) title.textContent = (kind === 'board' ? title.dataset.titleB : title.dataset.titleA) || title.textContent || '';
    }
    wrap.hidden = !result;
    if (empty instanceof HTMLElement) empty.hidden = !result || hits.length > 0;
    if (count) count.textContent = result && main.length ? `${shown.length} / ${main.length}` : '';
    if (all instanceof HTMLAnchorElement) {
      const href = this.tool === 'helmet' || kind !== 'board' ? all.dataset.hrefA : all.dataset.hrefB;
      if (href) all.href = href;
      all.hidden = !result || !href;
    }
  }
}

if (!customElements.get('bl-finder')) customElements.define('bl-finder', BlFinder);
